import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableRow,
  Checkbox,
  LinearProgress,
  TablePagination,
  Card,
  Divider,
  CardContent,
  CardHeader,
} from '@material-ui/core';

import SimpleTableHead from './SimpleTableHead';
import useTableStyles from './useTableStyles';
import clsx from 'clsx';
import _ from 'lodash';
import { DashboardHelpers } from 'utils';

const SimpleTable = ({
  title,
  customHeader,
  tableHeader,
  data,
  isLoading,
  showCheckbox,
  selected,
  selectRow,
  selectCheckbox,
  selectAll,
  selectAllChecked,
  sortFunc,
  defaultOrder,
  defaultOrderBy,
  saveOrderBy,
  saveOrder,
  rowStyle,
  paginate,
  fetchNextPage,
  isServerFed,
  setPage,
  pageSize,
  setPageSize,
}) => {
  const classes = useTableStyles();
  const [order, setOrder] = useState(defaultOrder);
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [localPage, setLocalPage] = useState(0);
  const [localPageSize, setLocalPageSize] = useState(pageSize || 10);
  const [startIndex, setStartIndex] = useState(0);

  const defaultPaginate = (newPage) => {
    const startIndex = newPage * localPageSize;
    setStartIndex(startIndex);
    setLocalPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    if (setPage) {
      setPage(0);
    }
    if (setPageSize) {
      setPageSize(event.target.value);
    }
    setLocalPageSize(event.target.value);
    setStartIndex(0);
    setLocalPage(0);
  };

  const handlePaginate = (newPage) => {
    if (setPage) {
      setPage(newPage);
    }
    if (isServerFed && fetchNextPage && newPage === localPageSize % _.size(data)) {
      fetchNextPage();
    }
    if (paginate) {
      paginate(newPage);
    } else {
      defaultPaginate(newPage);
    }
  };

  const handleRequestSort = (event, property) => {
    if (setOrderBy) {
      setOrderBy(property);
      saveOrderBy('setOrderBy', property);
    }
    if (setOrder) {
      setOrder(order === 'desc' ? 'asc' : 'desc');
      saveOrder('setOrder', order === 'desc' ? 'asc' : 'desc');
    }
  };

  const handleClick = (event, item) => {
    if (selectRow) {
      selectRow(event, item);
    }
  };

  const handleCheckbox = (item) => {
    if (selectCheckbox) {
      selectCheckbox(item);
    }
  };

  const isSelected = (id) => {
    if (_.isArray(selected)) {
      return selected.indexOf(id) !== -1;
    }
    return selected === id;
  };
  const allSortedData = sortFunc(tableHeader, order, orderBy, data);
  const sortedData = _.slice(allSortedData, startIndex, startIndex + localPageSize);
  const items =
    sortedData &&
    _.size(sortedData) > 0 &&
    sortedData.map((n) => {
      const _selected = n && isSelected(n.id);
      if (n) {
        return (
          <TableRow
            hover
            role="checkbox"
            tabIndex={-1}
            key={n.id}
            selected={_selected}
            className={n.is_archived ? classes.isArchived : rowStyle}
          >
            {showCheckbox && (
              <TableCell padding="checkbox">
                <Checkbox
                  aria-checked={_selected}
                  checked={_selected}
                  onChange={() => {
                    handleCheckbox(n);
                  }}
                />
              </TableCell>
            )}
            {tableHeader.map(
              (header, propIndex) =>
                !header.noDisplay && (
                  <TableCell
                    key={propIndex}
                    className={clsx(classes.tableCell, classes[header.className])}
                    style={_.has(n[header.dataAlias], 'style') ? n[header.dataAlias].style : {}}
                    scope="row"
                    onClick={(event) => {
                      header?.allowClick === undefined || header?.allowClick ? handleClick(event, n) : null;
                    }}
                  >
                    {header.dataRender ? header.dataRender(n) : _.get(n, header.dataAlias)}
                  </TableCell>
                )
            )}
          </TableRow>
        );
      }
      return undefined;
    });

  return (
    <Card>
      {customHeader(title)}
      <Divider />
      <CardContent className={classes.content}>
        <TableContainer className={classes.root}>
          <Table className={classes.table} aria-labelledby="tableTitle" stickyHeader padding="normal">
            <SimpleTableHead
              classes={classes}
              headers={tableHeader}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              showCheckbox={showCheckbox}
              selectAll={selectAll}
              checked={selectAllChecked}
            />
            {!isLoading && <TableBody>{items}</TableBody>}
          </Table>
          {isLoading && <LinearProgress />}
        </TableContainer>
        <TablePagination
          className={classes.spacing}
          component="div"
          count={data ? data.length : 0}
          page={localPage}
          onPageChange={(event, number) => handlePaginate(number)}
          rowsPerPage={localPageSize}
          onRowsPerPageChange={(size) => handlePageSizeChange(size)}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </CardContent>
    </Card>
  );
};

SimpleTable.propTypes = {
  title: PropTypes.string,
  customHeader: PropTypes.func,
  tableHeader: PropTypes.arrayOf(
    PropTypes.shape({
      alias: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      sortable: PropTypes.bool.isRequired,
      format: PropTypes.oneOf(['date', 'string', 'number']),
      dataAlias: PropTypes.string.isRequired,
      className: PropTypes.string,
      dataRender: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  isLoading: PropTypes.bool,
  defaultOrderBy: PropTypes.string,
  defaultOrder: PropTypes.string,
  saveOrderBy: PropTypes.func,
  saveOrder: PropTypes.func,
  showCheckbox: PropTypes.bool,
  selected: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.string]),
  selectRow: PropTypes.func.isRequired,
  selectCheckbox: PropTypes.func,
  selectAll: PropTypes.func,
  selectAllChecked: PropTypes.bool,
  sortFunc: PropTypes.func,
  rowStyle: PropTypes.string,
  paginate: PropTypes.func,
  fetchNextPage: PropTypes.func,
  isServerFed: PropTypes.bool,
  pageSize: PropTypes.number,
  setPageSize: PropTypes.func,
  page: PropTypes.number,
  setPage: PropTypes.func,
};

SimpleTable.defaultProps = {
  title: '',
  customHeader: (title) => <CardHeader title={title} />,
  tableHeader: undefined,
  data: undefined,
  isLoading: false,
  startIndex: 0,
  pageSize: 0,
  page: 0,
  defaultOrder: 'desc',
  defaultOrderBy: '',
  showCheckbox: false,
  selected: undefined,
  selectCheckbox: undefined,
  selectAll: undefined,
  selectAllChecked: false,
  sortFunc: (tableHeader, order, orderBy, data) => {
    return DashboardHelpers.sortTableFunction(tableHeader, order, orderBy, data);
  },
  paginate: undefined,
  fetchNextPage: undefined,
  isServerFed: false,
  rowStyle: undefined,
  saveOrderBy: () => {},
  saveOrder: () => {},
};

export default SimpleTable;
